import { useState } from "react";
import { IconButton, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";

interface ImageCarouselProps {
  images: string[];
  label: string;
}

export default function ImageCarousel({ images, label }: ImageCarouselProps) {
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", { noSsr: true });
  const [paused, setPaused] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [selectedItem, setSelectedItem] = useState(0);
  const playing = !paused && !prefersReducedMotion;

  const changeSlide = (direction: number) => {
    setSelectedItem((current) => (current + direction + images.length) % images.length);
  };

  return (
    <section
      className="photo-gallery"
      aria-label={label}
      aria-roledescription="carousel"
      onFocusCapture={() => setHasFocus(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setHasFocus(false);
      }}
    >
      <div className="photo-gallery-inner">
        <Carousel
          showThumbs={false}
          showArrows={false}
          showIndicators={false}
          showStatus={false}
          autoPlay={playing && !hasFocus}
          interval={6000}
          transitionTime={prefersReducedMotion ? 0 : 400}
          dynamicHeight
          infiniteLoop
          swipeable
          emulateTouch
          selectedItem={selectedItem}
          onChange={setSelectedItem}
        >
          {images.map((image, index) => (
            <div key={image} aria-hidden={index !== selectedItem}>
              <img src={image} alt={`Positive NRG - ${label}, ${index + 1} of ${images.length}`} loading="lazy" decoding="async" />
            </div>
          ))}
        </Carousel>
        <div className="gallery-toolbar">
          <div className="gallery-controls">
            {!prefersReducedMotion && (
              <IconButton aria-label={playing ? "Pause slideshow" : "Play slideshow"} onClick={() => setPaused((current) => !current)}>
                {playing ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
            )}
            <IconButton aria-label="Previous photo" onClick={() => changeSlide(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <span className="gallery-counter" aria-live={playing && !hasFocus ? "off" : "polite"} aria-atomic="true">
              {selectedItem + 1} / {images.length}
            </span>
            <IconButton aria-label="Next photo" onClick={() => changeSlide(1)}>
              <ArrowForwardIcon />
            </IconButton>
          </div>
        </div>
      </div>
    </section>
  );
}
